import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Typography from '@mui/material/Typography';
import { LightGrayLabel } from 'components/labels';
import { Text } from 'components/Text';
import { TrackingEvents, useTrackingDispatch } from 'modules/analytics';
import { getTrans, getTransWithParams } from 'modules/i18n';
import {
  GENERAL_STATUS_MAP,
  GeneralStatus,
  StatusBadge,
  useStatusLabel
} from 'modules/status-badge';
import { useWeekDetailsQuery } from 'modules/week-details/query';
import { getAutoCloseLabel } from 'modules/week-details/utils';
import { useAccessToken } from 'utils/auth';
import { DEFAULT_TIMEZONE, getHoursAndMinutesLabel } from 'utils/date';
import { useCountUp } from 'utils/motion';
import { useWorkPeriods } from '../hooks';
import { Month, Week } from '../types';
import {
  getMonthLabelWithTimezone,
  getWeekLabelWithTimezone,
  getWorkedTimeLabel
} from '../utils';
import { WorkWeeksSkeleton } from './WorkWeeksSkeleton';

type WeekWithId = Week & { weekId: string };

/**
 * Visual redesign of the weeks overview (data + actions unchanged):
 *  - The single actionable week (status OPEN) is lifted to the top as a bold blue
 *    "hero" — worked-vs-expected is the dominant metric. Blue = the present tense.
 *  - Every other week collapses into a quiet, scannable month-grouped ledger — white
 *    surfaces = the settled past. Status pills carry the at-a-glance scanning.
 * Tapping any week still navigates to its detail screen (where the actions live).
 */

const calendarWeekTitle = (week: Week) =>
  getTransWithParams('abbreviations.calendarWeekWithNumber', {
    weekNumber: week.calendarWeek
  });

// Shared click behaviour — identical tracking + navigation to the previous card.
const useWeekClick = () => {
  const navigate = useNavigate();
  const dispatch = useTrackingDispatch();

  return (weekId: string) => () => {
    dispatch({
      type: TrackingEvents.WEEK_BUTTON_CLICKED,
      payload: { weekId }
    });
    navigate(`${weekId}`);
  };
};

const HeroWeekCard = ({ week }: { week: WeekWithId }) => {
  const navigate = useNavigate();
  const dispatch = useTrackingDispatch();
  const statusLabel = useStatusLabel(week.weekStatus, GENERAL_STATUS_MAP);
  // The weeks-list payload only has shift minutes, not justified deviations or the
  // auto-close time, so fetch the week's detail for both. This shares react-query's cache,
  // so tapping into the week is then instant.
  const { token } = useAccessToken();
  const weekDetailsQuery = useWeekDetailsQuery(
    token,
    week.calendarWeek,
    week.year
  );
  const assignment = weekDetailsQuery.data?.data?.assignments?.[week.weekId];
  // Fold justified deviations (reasons) into the worked total so the hero matches the
  // detail screen's progress bar. Until the detail query resolves we show shift-only
  // minutes from the list; the count-up then glides up to include the deviations.
  const reportedReasonMinutes = assignment?.reportedReasonMinutes ?? 0;
  const actualWorkedMinutes = week.reportedMinutes + reportedReasonMinutes;
  // Worked hours count up on load; the bar fills to match.
  const animatedMinutes = useCountUp(actualWorkedMinutes);

  if (!week.calendarWeekStart || !week.calendarWeekEnd) return null;

  const dateLabel = getWeekLabelWithTimezone(
    week.calendarWeekStart,
    week.calendarWeekEnd,
    DEFAULT_TIMEZONE
  );
  const progress =
    week.expectedMinutes > 0
      ? Math.min(100, (animatedMinutes / week.expectedMinutes) * 100)
      : 0;
  // How many hours are still missing before the week can be finished — surfaced as a
  // hint below the metric. The action itself lives on the detail screen.
  const remainingMinutes = week.expectedMinutes - actualWorkedMinutes;
  // Same auto-close countdown shown on the week-detail screen ("Auto-Close week in: …").
  const autoClosing = assignment?.weekAutoClosing ?? week.weekAutoClosing;
  const autoCloseLabel = getAutoCloseLabel(week.weekStatus, autoClosing);

  const openDetail = () => {
    dispatch({
      type: TrackingEvents.WEEK_BUTTON_CLICKED,
      payload: { weekId: week.weekId }
    });
    navigate(`${week.weekId}`);
  };

  return (
    <Card
      onClick={openDetail}
      data-testid={`week-card-${week.weekId}`}
      sx={{
        cursor: 'pointer',
        mb: 4,
        p: 3,
        borderRadius: 3,
        bgcolor: 'primary.main',
        color: 'primary.contrastText',
        boxShadow: theme => theme.customShadows?.primary ?? theme.shadows[8],
        transition: 'transform .15s ease, box-shadow .15s ease',
        // Only lift on devices with a real pointer; on touch a :hover sticks after the tap.
        '@media (hover: hover)': {
          '&:hover': { transform: 'translateY(-2px)' }
        }
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: 1.5,
          mb: 3
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="h4" sx={{ color: '#fff', fontWeight: 800 }}>
            {calendarWeekTitle(week)}
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: 'rgba(255, 255, 255, 0.8)' }}
          >
            {dateLabel}
          </Typography>
        </Box>
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.75,
            px: 1.25,
            py: 0.5,
            borderRadius: 5,
            bgcolor: 'rgba(255, 255, 255, 0.18)',
            flexShrink: 0
          }}
        >
          <Box
            sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#fff' }}
          />
          <Typography
            variant="caption"
            sx={{ color: '#fff', fontWeight: 700, lineHeight: 1 }}
          >
            {statusLabel}
          </Typography>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75 }}>
        <Typography
          sx={{
            color: '#fff',
            fontWeight: 800,
            fontSize: '2.75rem',
            lineHeight: 1.1
          }}
        >
          {getHoursAndMinutesLabel(Math.round(animatedMinutes))}
        </Typography>
        <Typography variant="h6" sx={{ color: 'rgba(255, 255, 255, 0.85)' }}>
          / {getHoursAndMinutesLabel(week.expectedMinutes)}
        </Typography>
      </Box>
      {remainingMinutes > 0 && (
        <Typography
          variant="body2"
          sx={{ color: 'rgba(255, 255, 255, 0.8)', mt: 0.5 }}
        >
          {getTransWithParams('weeksOverview.hoursLeft', {
            hours: getHoursAndMinutesLabel(remainingMinutes)
          })}
        </Typography>
      )}

      <Box
        sx={{
          mt: 1.5,
          height: 8,
          borderRadius: 4,
          bgcolor: 'rgba(255, 255, 255, 0.22)',
          overflow: 'hidden'
        }}
      >
        <Box
          sx={{
            width: `${progress}%`,
            height: '100%',
            borderRadius: 4,
            bgcolor: '#fff'
          }}
        />
      </Box>

      {/* Always rendered with a reserved line so the card doesn't grow when the auto-close
          label arrives with the detail query; nowrap keeps a refined label on one line. */}
      <Typography
        variant="caption"
        sx={{
          display: 'block',
          mt: 2,
          minHeight: '1.25em',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          color: 'rgba(255, 255, 255, 0.7)'
        }}
      >
        {autoCloseLabel}
      </Typography>
    </Card>
  );
};

const LedgerRow = ({
  week,
  isFirst
}: {
  week: WeekWithId;
  isFirst: boolean;
}) => {
  const onWeekClick = useWeekClick();

  if (!week.calendarWeekStart || !week.calendarWeekEnd) return null;

  const dateLabel = getWeekLabelWithTimezone(
    week.calendarWeekStart,
    week.calendarWeekEnd,
    DEFAULT_TIMEZONE
  );

  return (
    <Box
      onClick={onWeekClick(week.weekId)}
      data-testid={`week-card-${week.weekId}`}
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 2,
        px: 2.5,
        py: 2,
        // Shared row height so the week-details shift rows stay consistent with this.
        minHeight: 88,
        cursor: 'pointer',
        borderTop: isFirst ? 'none' : '1px solid',
        borderColor: 'divider',
        transition: 'background-color .15s ease',
        // Real-pointer only, so the highlight doesn't stick after a tap on touch.
        '@media (hover: hover)': {
          '&:hover': { bgcolor: 'action.hover' }
        }
      }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Text variant="subtitle2" color="text.primary">
          {calendarWeekTitle(week)}
        </Text>
        <Text variant="body2" color="text.secondary">
          {dateLabel}
        </Text>
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-end',
          gap: 0.75,
          flexShrink: 0
        }}
      >
        <StatusBadge status={week.weekStatus} statusMap={GENERAL_STATUS_MAP} />
        <Text variant="body2" color="text.secondary">
          {getWorkedTimeLabel(week.expectedMinutes, week.reportedMinutes)}
        </Text>
      </Box>
    </Box>
  );
};

export const MonthView = ({
  month,
  heroWeekId
}: {
  month: Month;
  heroWeekId?: string;
}) => {
  const { workPeriods } = useWorkPeriods();

  if (!workPeriods) return null;

  const { weeks } = workPeriods;
  // The hero week is rendered on its own above the ledger — keep it out of its month.
  const weekIds = month.weeks.filter(weekId => weekId !== heroWeekId);

  if (!weekIds.length) return null;

  const label = getMonthLabelWithTimezone(
    month.month,
    month.year,
    DEFAULT_TIMEZONE
  );

  return (
    <Box sx={{ mb: 4 }}>
      <Text
        variant="subtitle2"
        color="text.secondary"
        sx={{
          display: 'block',
          mb: 1,
          ml: 0.5,
          textTransform: 'uppercase',
          letterSpacing: '0.06em'
        }}
      >
        {label}
      </Text>
      <Card
        sx={{
          borderRadius: 2,
          overflow: 'hidden',
          boxShadow: theme => theme.customShadows?.card ?? theme.shadows[1]
        }}
      >
        {weekIds.map((weekId: string, index) => (
          <LedgerRow
            key={'week-' + weekId}
            week={{ ...weeks[weekId], weekId }}
            isFirst={index === 0}
          />
        ))}
      </Card>
    </Box>
  );
};

export const WorkWeeks = () => {
  const { workPeriods, isLoading } = useWorkPeriods();

  if (isLoading) {
    return <WorkWeeksSkeleton />;
  }

  const months = workPeriods?.months || [];

  if (workPeriods && !months.length)
    return (
      <Box sx={{ textAlign: 'center', p: '0 30px', mt: 4 }}>
        <LightGrayLabel>{getTrans('noWeeksFound.label.body1')}</LightGrayLabel>
        <br />
        <LightGrayLabel>{getTrans('noWeeksFound.label.body2')}</LightGrayLabel>
      </Box>
    );

  // The one week you can act on right now (status OPEN) becomes the hero.
  let heroWeekId: string | undefined;
  if (workPeriods) {
    for (const month of months) {
      const open = month.weeks.find(
        weekId => workPeriods.weeks[weekId]?.weekStatus === GeneralStatus.OPEN
      );
      if (open) {
        heroWeekId = open;
        break;
      }
    }
  }

  return (
    <Box>
      {workPeriods && heroWeekId && (
        <HeroWeekCard
          week={{ ...workPeriods.weeks[heroWeekId], weekId: heroWeekId }}
        />
      )}
      {months.map((month: Month, index: number) => (
        <MonthView
          key={'month-' + month.month + '-' + index}
          month={month}
          heroWeekId={heroWeekId}
        />
      ))}
    </Box>
  );
};
